<template>
  <tutorial-layout title="Props" :referenceLinks="referenceLinks" :youtubeLinks="youtubeLinks">
    <p slot="overview">
      Props are how we pass variables and other information around between different components. This is similar to how in Javascript we can pass variables into functions as arguments
    </p>
    <p slot="benefits">
      <ul>
        <li>Props are passed down the component tree to descendents (not up)</li>
        <li>Props are read-only and cannot be modified</li>
      </ul>
    </p>
    <v-card>
      <v-card-text>
        <h2>Parent Component</h2><br/>
        <p>
          If you want to pass a value from your component into a child component, it's exactly like adding an HTML attribute:
        </p>
        <pre>
            <code>&lt;template&gt;
    &lt;Person name="Vin Diesel" age="53" phone="424-288-2000"&gt;&lt;/Person&gt;
&lt;/template&gt;<br/>
&lt;script&gt;
  import Person from "./person";
  export default {
    name: "MainComponent",
    components: { Person },
    created() {},
    data() {},
    props: {},
    watch: {},
    computed: {},
    methods: {},
  };
&lt;/script&gt;
</code>
        </pre>
        <br/>
        <h2>Child Component</h2><br/>
        <p>Person is the name of the child component. It will then use the name, age and phone props to render itself to the page. It might render something like this</p>
      <pre>
            <code>&lt;template&gt;
    &lt;v-card color="purple"&gt;
        &lt;v-list-item&gt;
            &lt;v-list-item-avatar&gt;&lt;v-icon&gt;mdi-account&lt;/v-icon&gt;&lt;/v-list-item-avatar&gt;
            &lt;v-list-item-content&gt;
                &lt;v-list-item-title&gt;{{testname}}&lt;/v-list-item-title&gt;
                &lt;v-list-item-subtitle&gt;&lt;b&gt;Age&lt;/b&gt;: {{testage}}, &lt;b&gt;Phone&lt;/b&gt;: {{testphone}}&lt;/v-list-item-subtitle&gt;
            &lt;/v-list-item-content&gt;
        &lt;/v-list-item&gt;
    &lt;/v-card&gt;
&lt;/template&gt;<br/>
&lt;script&gt;
  export default {
    name: 'Person',
    components: {},
    props : {
      name : {type:String},
      age : {type:Number},
      phone : {type:String},
    },
    computed : {},
  };
&lt;/script&gt;
</code>
</pre>
<br/>
        <h2>Output</h2><br/>
<Person name="Vin Diesel" age="53" phone="424-288-2000"></Person>
        </v-card-text>
          </v-card>
<br />

<h2>Default Values</h2><br/>
<v-card>
	<v-card-text>
		<p>We can set a default value on our optional props, since they may not be provided each time.</p>
    <pre>
    <code>&lt;script&gt;
  export default {
    name: 'Person',
    components: {},
    props : {
      name : {type:String, default: "Sample Name"},
      age : {type:Number, default: "Sample Age"},
      phone : {type:String, default: "Sample Phone Number"},
    },
    computed : {},
  };
&lt;/script&gt;
</code>
</pre>
	</v-card-text>
</v-card>

<br/>
<h2>Required Props</h2><br/>
<v-card>
	<v-card-text>
		<p>If the props is necessary we can set required in our prop definition.</p>
		<pre>
    <code>&lt;script&gt;
  export default {
    name: 'Person',
    components: {},
    props : {
      name : {type:String, required: true},
      age : {type:Number, required: true},
      phone : {type:String, required: true},
    },
    computed : {},
  };
&lt;/script&gt;
</code>
</pre>
	</v-card-text>
</v-card>

<br/>
<h2>Using props outside of the template</h2><br/>
<v-card>
	<v-card-text>
    <h2>Parent Component</h2><br/>
    <pre>
            <code>&lt;template&gt;
    &lt;Person name="Tom Cruise" age="58" phone="(212) 277-7555"&gt;&lt;/Person&gt;
&lt;/template&gt;<br/>
&lt;script&gt;
  import Person from "./person";
  export default {
    name: "MainComponent",
    components: { Person },
  };
&lt;/script&gt;
</code>
        </pre>
        <br/>
    <h2>Child Component</h2><br/>
		<p>While being able to use props inside of your template is great, the real power comes from using them in your methods, computed props, and other Javascript used in your component. We can use the name, age and phone via computed. Therefore the template looks like:</p>

    <pre>
    <code>&lt;template&gt;
    &lt;v-card color="purple"&gt;
        &lt;v-list-item&gt;
            &lt;v-list-item-avatar&gt;&lt;v-icon&gt;mdi-account&lt;/v-icon&gt;&lt;/v-list-item-avatar&gt;
            &lt;v-list-item-content&gt;
                &lt;v-list-item-title&gt;{{testname2}}&lt;/v-list-item-title&gt;
                &lt;v-list-item-subtitle&gt;&lt;b&gt;Age&lt;/b&gt;: {{testage2}}, &lt;b&gt;Phone&lt;/b&gt;: {{testphone2}}&lt;/v-list-item-subtitle&gt;
            &lt;/v-list-item-content&gt;
        &lt;/v-list-item&gt;
    &lt;/v-card&gt;
&lt;/template&gt;<br/>
&lt;script&gt;
  export default {
    name: 'Person',
    components: {},
    props : {
      name: { type: String, default: "" },
      age: { type: Number, default: "" },
      phone: { type: String, default: "" },
    },
    computed : {
      getName() {
        return this.name;
      },
      getAge() {
        return this.age;
      },
      getPhone() {
        return this.phone;
      },
    },
  };
&lt;/script&gt;</code>
</pre>
<br/>
		<h2>Output</h2><br/>
    <User name="Tom Cruise" age="58" phone="(212) 277-7555"></User>
    
	</v-card-text>
</v-card>
  </tutorial-layout>

</template>

<script>
import moment from "moment";

import uiView from "@/_mixins/ui-view-mixin";
import tutorialLayout from "../../components/tutorial-layout";
import Person from "./person";
import User from "./user";
export default {
  name: "Props",
  mixins: [uiView],
  created() {},
  data() {
    return {
      text1: "Example Text",
      testname: "{{ name }}",
      testage: "{{ age }}",
      testphone: "{{ phone }}",
      testname2: "{{ getName }}",
      testage2: "{{ getAge }}",
      testphone2: "{{ getPhone }}",
    };
  },
  components: { tutorialLayout, Person, User },
  props: {},
  watch: {},
  computed: {
    referenceLinks() {
      return [
        {
          title: "Props",
          href: "https://vuejs.org/v2/guide/components-props.html",
        },
        {
          title: "Vue-Component Props",
          href: "https://flaviocopes.com/vue-props/",
        },
        {
          title: "Props usuage",
          href:
            "https://blog.logrocket.com/how-to-use-props-to-pass-data-to-child-components/",
        },
      ];
    },
    youtubeLinks() {
      return [
        {
          title: "Vue Props in 10 minutes",
          href: "https://www.youtube.com/embed/W2j7CjQI2y4",
        },
        {
          title: "Component Communication",
          href: "https://www.youtube.com/embed/0HMkrrx6krw",
        },
        {
          title: "Props for Beginners",
          href: "https://www.youtube.com/embed/q5AQv5IOd-M",
        },
      ];
    },
  },
  methods: {},
};
</script>
<style lang="scss">
</style>

